import { useRef, useState } from 'react';
import type * as React from 'react';
import { useGarden } from '../../garden/GardenContext';
import type { GardenElement } from '../../garden/gardenReducer';
import { ELEMENT_META_BY_TYPE, MIN_ELEMENT_SIZE, DRAG_THRESHOLD_PX } from '../../garden/constants';
import { ICONS_BY_TYPE } from '../../icons/IconLibrary';
import { boundingBoxOf, clampPointToBox, clampBoxToBounds } from '../../utils/geometry';
import type { Point } from '../../utils/geometry';
import './PlacedElement.css';

interface Size {
  width: number;
  height: number;
}

interface PlacedElementProps {
  element: GardenElement;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function PlacedElement({ element }: PlacedElementProps) {
  const {
    shape,
    selectedElementIds,
    selectElement,
    moveElement,
    resizeElement,
    groupDrag,
    beginGroupDrag,
    updateGroupDrag,
    commitGroupDrag,
    cancelGroupDrag,
    zoom,
  } = useGarden();
  const [dragPos, setDragPos] = useState<Point | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [resizePreview, setResizePreview] = useState<Size | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  const startClientRef = useRef<Point>({ x: 0, y: 0 });
  const startElementRef = useRef<Point>({ x: 0, y: 0 });
  const startSizeRef = useRef<Size>({ width: 0, height: 0 });
  const hasMovedRef = useRef(false);
  const draggingRef = useRef(false);
  const ctrlModeRef = useRef(false);
  const isGroupLeaderRef = useRef(false);
  const lastPosRef = useRef<Point>({ x: element.x, y: element.y });

  const meta = ELEMENT_META_BY_TYPE[element.type];
  const Icon = ICONS_BY_TYPE[element.type];
  const isSelected = selectedElementIds.includes(element.id);
  const isSoleSelection = isSelected && selectedElementIds.length === 1;

  let position: Point = { x: element.x, y: element.y };
  if (dragPos) {
    position = dragPos;
  } else if (groupDrag && groupDrag.ids.includes(element.id)) {
    const start = groupDrag.startPositions[element.id];
    position = { x: start.x + groupDrag.dx, y: start.y + groupDrag.dy };
  }

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const isCtrl = event.ctrlKey || event.metaKey;
    ctrlModeRef.current = isCtrl;
    hasMovedRef.current = false;
    startClientRef.current = { x: event.clientX, y: event.clientY };

    if (isCtrl) {
      draggingRef.current = false;
      return;
    }

    draggingRef.current = true;
    setIsDragging(true);
    const isMultiSelected =
      selectedElementIds.length > 1 && selectedElementIds.includes(element.id);
    if (isMultiSelected) {
      isGroupLeaderRef.current = true;
      beginGroupDrag(selectedElementIds);
    } else {
      isGroupLeaderRef.current = false;
      startElementRef.current = { x: element.x, y: element.y };
    }
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (ctrlModeRef.current || !draggingRef.current) return;
    // Threshold check uses raw screen-space movement (physical mouse travel);
    // position math converts to world-space by dividing out the zoom factor.
    const screenDx = event.clientX - startClientRef.current.x;
    const screenDy = event.clientY - startClientRef.current.y;
    if (Math.abs(screenDx) > DRAG_THRESHOLD_PX || Math.abs(screenDy) > DRAG_THRESHOLD_PX) {
      hasMovedRef.current = true;
    }
    const dx = screenDx / zoom;
    const dy = screenDy / zoom;
    if (isGroupLeaderRef.current) {
      updateGroupDrag(dx, dy);
      return;
    }
    const box = boundingBoxOf(shape);
    const targetX = startElementRef.current.x + dx;
    const targetY = startElementRef.current.y + dy;
    // meta.resizable guarantees element.width/height are set.
    const clamped = meta.resizable
      ? clampBoxToBounds(targetX, targetY, element.width!, element.height!, box)
      : clampPointToBox(targetX, targetY, box);
    lastPosRef.current = clamped;
    setDragPos(clamped);
  }

  function handlePointerUp(event: React.PointerEvent<HTMLDivElement>) {
    event.stopPropagation();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    draggingRef.current = false;
    setIsDragging(false);

    if (ctrlModeRef.current) {
      selectElement(element.id, true);
      return;
    }

    if (isGroupLeaderRef.current) {
      isGroupLeaderRef.current = false;
      if (hasMovedRef.current) {
        commitGroupDrag();
      } else {
        cancelGroupDrag();
        selectElement(element.id, false);
      }
      return;
    }

    if (hasMovedRef.current) {
      moveElement(element.id, lastPosRef.current.x, lastPosRef.current.y);
    } else {
      selectElement(element.id, false);
    }
    setDragPos(null);
  }

  function handleResizePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    startClientRef.current = { x: event.clientX, y: event.clientY };
    // The resize handle only renders for resizable elements, so width/height exist.
    startSizeRef.current = { width: element.width!, height: element.height! };
    setIsResizing(true);
  }

  function handleResizePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!isResizing) return;
    const dx = (event.clientX - startClientRef.current.x) / zoom;
    const dy = (event.clientY - startClientRef.current.y) / zoom;
    const box = boundingBoxOf(shape);
    const maxWidth = Math.max(box.maxX - box.minX, MIN_ELEMENT_SIZE);
    const maxHeight = Math.max(box.maxY - box.minY, MIN_ELEMENT_SIZE);
    const width = clamp(startSizeRef.current.width + dx, MIN_ELEMENT_SIZE, maxWidth);
    const height = clamp(startSizeRef.current.height + dy, MIN_ELEMENT_SIZE, maxHeight);
    setResizePreview({ width, height });
  }

  function handleResizePointerUp(event: React.PointerEvent<HTMLDivElement>) {
    event.stopPropagation();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsResizing(false);
    if (resizePreview) {
      resizeElement(element.id, resizePreview.width, resizePreview.height);
    }
    setResizePreview(null);
  }

  const classNames = [
    'placed-element',
    meta.resizable ? 'placed-element--resizable' : 'placed-element--point',
    isSelected ? 'placed-element--selected' : '',
    isDragging ? 'placed-element--dragging' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (meta.resizable) {
    const size: Size = resizePreview || { width: element.width!, height: element.height! };
    return (
      <div
        className={classNames}
        style={{ left: position.x, top: position.y, width: size.width, height: size.height }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <Icon width={size.width} height={size.height} />
        <span className="placed-element__label placed-element__label--overlay">{element.name}</span>
        {isSoleSelection && (
          <div
            className="placed-element__resize-handle"
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerUp}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={classNames}
      style={{ left: position.x, top: position.y }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <Icon size={36} />
      <span className="placed-element__label">{element.name}</span>
    </div>
  );
}

export default PlacedElement;
